import { useEffect, useRef, useState } from "react";
import { CheckCircle2, PhoneOff, RotateCcw, XCircle, type LucideIcon } from "lucide-react";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import { Modal } from "@/components/ui/Modal";
import { WhatsAppIcon } from "@/components/ui/WhatsAppIcon";
import { useToast } from "@/components/ui/Toast";
import { CloseSaleForm } from "@/components/sales/CloseSaleForm";
import { draftFor } from "@/components/prospects/WhatsAppButton";
import { api } from "@/lib/api";
import { phoneUsable } from "@/lib/brief";
import {
  ABSENT_DAYS,
  BLOCKERS,
  LOSS_REASONS,
  RELANCE_TIMES,
  buildOutcomePayload,
  closedChoiceFor,
  defaultCloseDate,
  isAlreadySold,
  parisToday,
  relanceDate,
  relanceDays,
  type OutcomeFormState,
} from "@/lib/outcome-form";
import { cn, formatDateLong, formatEUR } from "@/lib/utils";
import { PRODUCTS } from "@shared/products";
import { SETTINGS_DEFAULTS, type ProductKey, type Prospect } from "@shared/schema";
import { hourLabel, type OutcomeChoice } from "@shared/outcome";
import { waLink } from "@shared/whatsapp";
import { notifyTodayChanged } from "@/lib/pointage";

const TILES: Array<{ choice: OutcomeChoice; label: [string, string?]; icon: LucideIcon; tone: string }> = [
  { choice: "closed_pc", label: ["Closé", "Power Coaching"], icon: CheckCircle2, tone: "text-[var(--color-success)]" },
  { choice: "closed_acc", label: ["Closé", "Accélérateur"], icon: CheckCircle2, tone: "text-[var(--color-success)]" },
  { choice: "relance", label: ["À relancer"], icon: RotateCcw, tone: "text-[var(--color-warning)]" },
  { choice: "lost", label: ["Perdu"], icon: XCircle, tone: "text-[var(--color-danger)]" },
  { choice: "no_show", label: ["Absent"], icon: PhoneOff, tone: "text-[var(--color-muted-foreground)]" },
];

interface SaleValues {
  product: ProductKey;
  totalAmount: number;
  commissionRate: number;
  closedAt: string;
  paymentPlan: number;
  notes: string;
}

function isClosed(choice: OutcomeChoice): choice is "closed_pc" | "closed_acc" {
  return choice === "closed_pc" || choice === "closed_acc";
}

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : "Erreur";
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors",
        active
          ? "border-[var(--color-navy-600)] bg-[var(--color-navy-600)] text-white"
          : "border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-navy-700)] hover:bg-[var(--color-muted)]",
      )}
    >
      {children}
    </button>
  );
}

function TimeSlot({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-lg border px-1 py-1.5 text-[13px] font-medium transition-colors",
        active
          ? "border-[var(--color-navy-600)] bg-[var(--color-navy-600)] text-white"
          : "border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-navy-700)] hover:bg-[var(--color-muted)]",
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
        {label}
      </div>
      {children}
    </div>
  );
}

export interface OutcomeModalProps {
  open: boolean;
  prospect: Prospect;
  /** RDV concerné : sans lui, aucune issue n'est écrite sur un RDV. */
  eventId?: number;
  /** Début du RDV (ISO), pour l'heure dans le message d'absence. */
  eventStart?: string | null;
  /** Tuile pré-sélectionnée (bouton cliqué dans la fiche). Par défaut « À relancer ». */
  initialChoice?: OutcomeChoice;
  /** Lien de réservation des messages ; lu dans les réglages s'il manque. */
  bookingUrl?: string;
  /**
   * Produit de la vente la plus récente d'un prospect closed_won (pré-sélection du « Closé »).
   * `undefined` : lu sur la fiche ; `null` : inconnu (Power Coaching).
   */
  lastSaleProduct?: ProductKey | null;
  onClose: () => void;
  /** Appelé après une issue enregistrée (ou une vente créée) : la page appelante se recharge. */
  onDone: () => void;
}

/** Issue d'un appel en un clic (maquette #issue). Le contenu est remonté à chaque ouverture. */
export function OutcomeModal(props: OutcomeModalProps) {
  if (!props.open) return null;
  return <OutcomeBody {...props} />;
}

function OutcomeBody({
  prospect,
  eventId,
  eventStart,
  initialChoice,
  bookingUrl: bookingUrlProp,
  lastSaleProduct,
  onClose,
  onDone,
}: OutcomeModalProps) {
  const toast = useToast();
  const today = useRef(parisToday()).current;
  // Déjà acheté à l'ouverture : les 5 tuiles restent là, mais changer l'issue demande une
  // confirmation (le serveur refuse sans `force`).
  const wonAtOpen = prospect.status === "closed_won";
  const [step, setStep] = useState<"choose" | "sale">("choose");
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState<OutcomeFormState>({
    choice: wonAtOpen
      ? // Client : le bouton cliqué, sinon son « Closé » (pas d'« Absent » sans RDV).
        initialChoice && !(initialChoice === "no_show" && eventId === undefined)
        ? initialChoice
        : closedChoiceFor(lastSaleProduct ?? "power_coaching")
      : // Pas d'« Absent » sans RDV : on retombe sur « À relancer ».
        initialChoice === "no_show" && eventId === undefined
        ? "relance"
        : (initialChoice ?? "relance"),
    relanceDay: today,
    relanceTime: "",
    absentDay: "today",
    blocker: null,
    note: "",
    lossReason: null,
    lossOther: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingUrl, setBookingUrl] = useState(bookingUrlProp ?? SETTINGS_DEFAULTS.calendly_booking_url);
  const [message, setMessage] = useState(() =>
    draftFor(prospect, "absence", eventStart ?? undefined, bookingUrlProp ?? SETTINGS_DEFAULTS.calendly_booking_url),
  );
  const messageEdited = useRef(false);
  // Change à chaque régénération du brouillon : remonte le champ avec le nouveau texte.
  const [draftVersion, setDraftVersion] = useState(0);
  // Produit de la vente créée pendant cette ouverture (état : l'écran en dépend).
  const [createdProduct, setCreatedProduct] = useState<ProductKey | null>(null);
  const createdRef = useRef<ProductKey | null>(null);
  const waOpened = useRef(false);

  const patch = (p: Partial<OutcomeFormState>) => {
    setForm((f) => ({ ...f, ...p }));
    setError(null);
  };

  // Produit de la dernière vente, lu sur la fiche quand la page appelante ne le donne pas.
  useEffect(() => {
    if (!wonAtOpen || lastSaleProduct !== undefined || initialChoice) return;
    let active = true;
    api
      .getProspect(prospect.id)
      .then((r) => {
        const product = r.sales[0]?.product;
        if (active && product) setForm((f) => ({ ...f, choice: closedChoiceFor(product) }));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [wonAtOpen, lastSaleProduct, initialChoice, prospect.id]);

  // Lien de réservation : repris des réglages quand la page appelante ne le donne pas.
  useEffect(() => {
    if (bookingUrlProp !== undefined) {
      setBookingUrl(bookingUrlProp);
      return;
    }
    let active = true;
    api
      .settings()
      .then((r) => {
        if (active && r.settings.calendly_booking_url) setBookingUrl(r.settings.calendly_booking_url);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [bookingUrlProp]);

  // Message d'absence : régénéré tant qu'il n'a pas été modifié à la main.
  useEffect(() => {
    if (!messageEdited.current) {
      setMessage(draftFor(prospect, "absence", eventStart ?? undefined, bookingUrl));
      setDraftVersion((v) => v + 1);
    }
  }, [prospect, eventStart, bookingUrl]);

  const choice = form.choice;
  const usablePhone = phoneUsable(prospect.phone);
  const name = prospect.firstName;
  // Vente déjà là (fiche closée avant d'ouvrir, ou créée ici) : « Closé » ne note que l'issue,
  // sinon un second passage créerait une seconde vente.
  const alreadySold = isAlreadySold(prospect.status, lastSaleProduct) || createdProduct !== null;
  const tiles = createdProduct
    ? TILES.filter((t) => t.choice === closedChoiceFor(createdProduct))
    : TILES.filter((t) => t.choice !== "no_show" || eventId !== undefined);
  // Client déjà gagné qui change d'issue : case « Je confirme » obligatoire, `force` envoyé.
  const needsConfirm = wonAtOpen && !isClosed(choice);
  const blocked = needsConfirm && !confirmed;

  /** Fermer après une vente créée ici : la page se recharge et on signale l'issue manquante. */
  function close() {
    if (createdProduct) {
      toast.error("Vente créée, mais l'issue du RDV n'est pas notée. Rouvre Issue pour la noter.");
      onDone();
    }
    onClose();
  }
  // Pendant un enregistrement : ni croix, ni fond, ni Échap.
  const guardedClose = busy ? () => {} : close;

  function finish(text: string) {
    toast.success(text);
    notifyTodayChanged();
    onDone();
    onClose();
  }

  async function save(withWhatsApp: boolean) {
    if (blocked) return;
    const built = buildOutcomePayload(form, today, eventId, needsConfirm);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    let href: string | null = null;
    if (withWhatsApp) {
      const text = message.trim();
      href = waLink(prospect.phone, text);
      if (!text || !href) {
        setError(!text ? "Écris le message avant d'ouvrir WhatsApp." : "Pas de numéro utilisable.");
        return;
      }
      // Ouvrir avant tout await : sinon Safari bloque la fenêtre (même ordre que WhatsAppButton).
      if (!waOpened.current) {
        const win = window.open(href, "_blank");
        if (win) win.opener = null;
        else {
          setError("Le navigateur a bloqué l'ouverture de WhatsApp. Autorise les fenêtres pour ce site, ou clique sur Enregistrer pour noter l'absence seule.");
          return;
        }
        waOpened.current = true;
      }
    }
    setBusy(true);
    setError(null);
    try {
      await api.setOutcome(prospect.id, built.data);
    } catch (e) {
      setBusy(false);
      setError(
        waOpened.current
          ? `WhatsApp est ouvert mais l'issue n'a pas été enregistrée : ${errMessage(e)}. Réessaie.`
          : errMessage(e),
      );
      return;
    }
    if (withWhatsApp) {
      try {
        await api.createInteraction({
          prospectId: prospect.id,
          kind: "message",
          body: "WhatsApp ouvert : absence",
        });
      } catch (e) {
        toast.error(
          `L'issue est enregistrée mais la note WhatsApp ne l'est pas : ${errMessage(e)}`,
        );
      }
    }
    setBusy(false);
    finish(createdProduct ? "Vente et issue enregistrées" : "Issue enregistrée");
  }

  async function submitSale(values: SaleValues) {
    // La vente d'abord (elle passe le prospect en closed_won), l'issue ensuite : sans vente, rien n'est écrit.
    setBusy(true);
    try {
      if (createdRef.current === null) {
        await api.createSale({ prospectId: prospect.id, ...values });
        createdRef.current = values.product;
        setCreatedProduct(values.product);
      }
      const saleChoice = closedChoiceFor(createdRef.current);
      try {
        await api.setOutcome(prospect.id, {
          choice: saleChoice,
          ...(eventId !== undefined ? { eventId } : {}),
        });
      } catch (e) {
        // Retour à l'écran d'issue, réduit au « Closé » de la vente : un nouvel essai ne recrée rien.
        setStep("choose");
        setForm((f) => ({ ...f, choice: saleChoice }));
        setError(`La vente est enregistrée, mais l'issue n'a pas pu être notée : ${errMessage(e)}. Réessaie.`);
        return;
      }
      finish("Vente et issue enregistrées");
    } finally {
      setBusy(false);
    }
  }

  if (step === "sale" && isClosed(choice)) {
    return (
      <Modal
        open
        onClose={guardedClose}
        title={`Close ${name}`}
        description="Crée la vente et son échéancier de paiement"
        size="lg"
        footer={
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => setStep("choose")}
          >
            Retour
          </button>
        }
      >
        <CloseSaleForm
          defaultProduct={choice === "closed_acc" ? "accelerateur" : "power_coaching"}
          defaultClosedAt={defaultCloseDate(eventStart)}
          onSubmit={submitSale}
        />
      </Modal>
    );
  }

  const chosenDate = relanceDate(form.relanceDay, today);
  const absentIsLast = choice === "no_show";

  return (
    <Modal
      open
      onClose={guardedClose}
      title={eventId !== undefined ? `Comment s'est passé l'appel avec ${name} ?` : `Où en est ${name} ?`}
      description="Un choix met à jour la fiche, le RDV et la prochaine action."
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={close} disabled={busy}>
            Annuler
          </button>
          {isClosed(choice) && !alreadySold ? (
            <button type="button" className="btn btn-primary" onClick={() => setStep("sale")}>
              Continuer vers la vente
            </button>
          ) : (
            <>
              {absentIsLast && (
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={busy || blocked || !usablePhone || !message.trim()}
                  title={usablePhone ? undefined : "Pas de numéro utilisable"}
                  onClick={() => save(true)}
                >
                  <WhatsAppIcon size={16} /> Enregistrer + WhatsApp
                </button>
              )}
              <button
                type="button"
                className={cn("btn", absentIsLast ? "btn-outline" : "btn-primary")}
                disabled={busy || blocked}
                onClick={() => save(false)}
              >
                {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
            </>
          )}
        </>
      }
    >
      <div
        role="radiogroup"
        aria-label="Issue de l'appel"
        className={cn(
          "grid grid-cols-2 gap-2",
          tiles.length >= 5 ? "sm:grid-cols-5" : tiles.length === 4 ? "sm:grid-cols-4" : "sm:grid-cols-2",
        )}
      >
        {tiles.map((t) => {
          const on = choice === t.choice;
          return (
            <button
              key={t.choice}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => {
                patch({ choice: t.choice });
                setConfirmed(false);
              }}
              className={cn(
                "flex flex-col items-center gap-1.5 rounded-[var(--radius-md)] border px-2 py-3 text-center text-[13.5px] font-semibold leading-tight transition-colors",
                on
                  ? "border-[var(--color-navy-600)] bg-[var(--color-navy-50)] shadow-[0_0_0_1px_var(--color-navy-600)]"
                  : "border-[var(--color-border-strong)] bg-[var(--color-surface)] hover:bg-[var(--color-muted)]",
              )}
            >
              <t.icon size={22} className={t.tone} aria-hidden />
              <span className="text-[var(--color-navy-700)]">
                {t.label[0]}
                {t.label[1] && (
                  <>
                    <br />
                    {t.label[1]}
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        {needsConfirm && (
          <div
            role="alert"
            className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-warning)] bg-[var(--color-warning-soft)] px-3 py-2.5 text-sm"
          >
            <p>
              Ce prospect a déjà acheté. Changer l'issue modifie son statut, la vente reste
              enregistrée.
            </p>
            <label className="mt-2 flex cursor-pointer items-center gap-2 font-semibold">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-warning)]"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              Je confirme
            </label>
          </div>
        )}
        {isClosed(choice) && alreadySold && (
          <Field label="Vente">
            <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2.5 text-sm">
              La vente est déjà enregistrée, je note seulement l'issue.
            </div>
          </Field>
        )}
        {isClosed(choice) && !alreadySold && (
          <Field label="La vente s'enregistre comme d'habitude">
            <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2.5 text-sm">
              Le formulaire de vente s'ouvre pré-rempli :{" "}
              <b>{PRODUCTS[choice === "closed_acc" ? "accelerateur" : "power_coaching"].label}</b>
              {" · "}
              {formatEUR(PRODUCTS[choice === "closed_acc" ? "accelerateur" : "power_coaching"].defaultPrice)}
              {" · commission "}
              {Math.round(
                PRODUCTS[choice === "closed_acc" ? "accelerateur" : "power_coaching"].defaultCommissionRate * 100,
              )}{" "}
              %. L'issue n'est notée qu'une fois la vente créée.
            </div>
          </Field>
        )}

        {choice === "relance" && (
          <>
            <Field label="Quel jour">
              <div className="grid grid-cols-7 gap-1.5">
                {relanceDays(today).map((d) => {
                  const on = form.relanceDay === d.date;
                  return (
                    <button
                      key={d.date}
                      type="button"
                      aria-pressed={on}
                      onClick={() => patch({ relanceDay: d.date })}
                      className={cn(
                        "min-w-0 rounded-[var(--radius-md)] border py-1.5 text-center transition-colors",
                        on
                          ? "border-[var(--color-navy-600)] bg-[var(--color-navy-600)] text-white"
                          : "border-[var(--color-border-strong)] text-[var(--color-navy-700)] hover:bg-[var(--color-muted)]",
                        !on && (d.weekend ? "bg-[var(--color-muted)]" : "bg-[var(--color-surface)]"),
                      )}
                    >
                      <span className="block text-[11px] capitalize opacity-80">
                        {d.date === today ? "Auj." : d.weekday}
                      </span>
                      <span className="block text-lg font-semibold leading-tight">{d.dayOfMonth}</span>
                      <span className="block text-[11px] capitalize opacity-80">{d.month}</span>
                    </button>
                  );
                })}
              </div>
              <label className="mt-2 flex items-center gap-2 text-[13px] text-[var(--color-muted-foreground)]">
                Autre date :
                <input
                  type="date"
                  className="input-base !h-9 !w-auto"
                  min={today}
                  value={form.relanceDay}
                  onChange={(e) => patch({ relanceDay: e.target.value })}
                />
              </label>
            </Field>
            <Field label="À quelle heure">
              <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
                <TimeSlot active={!form.relanceTime} onClick={() => patch({ relanceTime: "" })}>
                  Sans heure
                </TimeSlot>
                {RELANCE_TIMES.map((t) => (
                  <TimeSlot key={t} active={form.relanceTime === t} onClick={() => patch({ relanceTime: t })}>
                    {hourLabel(t)}
                  </TimeSlot>
                ))}
              </div>
              {chosenDate && (
                <p className="mt-2 rounded-[var(--radius-md)] bg-[var(--color-navy-50)] px-3 py-2 text-[13px] font-semibold text-[var(--color-navy-700)]">
                  Relance {chosenDate === today ? "aujourd'hui" : `le ${formatDateLong(chosenDate)}`}
                  {form.relanceTime ? ` à ${hourLabel(form.relanceTime)}` : ", sans heure"}
                </p>
              )}
            </Field>
            <Field label="Ce qui bloque">
              <div className="flex flex-wrap gap-2">
                {BLOCKERS.map((b) => (
                  <Chip
                    key={b}
                    active={form.blocker === b}
                    onClick={() => patch({ blocker: form.blocker === b ? null : b })}
                  >
                    {b}
                  </Chip>
                ))}
              </div>
            </Field>
            <Field label="Note (facultatif)">
              <DictationTextarea
                key="note-relance"
                className="input-base"
                rows={3}
                maxLength={2000}
                initialValue={form.note}
                onValueChange={(v) => patch({ note: v })}
                placeholder="Intéressé par l'Accélérateur, rappeler lundi après sa paie…"
                aria-label="Note"
              />
            </Field>
          </>
        )}

        {choice === "lost" && (
          <>
            <Field label="Raison">
              <div className="flex flex-wrap items-center gap-2">
                {LOSS_REASONS.filter((r) => r !== "Autre").map((r) => (
                  <Chip
                    key={r}
                    active={form.lossReason === r}
                    onClick={() => patch({ lossReason: form.lossReason === r ? null : r })}
                  >
                    {r}
                  </Chip>
                ))}
                {/* « Autre » en retrait : lien discret après les pastilles, même bascule. */}
                <button
                  type="button"
                  aria-pressed={form.lossReason === "Autre"}
                  onClick={() => patch({ lossReason: form.lossReason === "Autre" ? null : "Autre" })}
                  className={cn(
                    "ml-1 px-1 py-1.5 text-[12px] underline underline-offset-2 transition-colors",
                    form.lossReason === "Autre"
                      ? "font-semibold text-[var(--color-navy-700)] decoration-2"
                      : "text-[var(--color-muted-foreground)] hover:text-[var(--color-navy-700)]",
                  )}
                >
                  Autre…
                </button>
              </div>
              {form.lossReason === "Autre" && (
                <input
                  className="input-base mt-2"
                  maxLength={200}
                  value={form.lossOther}
                  onChange={(e) => patch({ lossOther: e.target.value })}
                  placeholder="Précise la raison"
                  aria-label="Autre raison"
                />
              )}
            </Field>
            <Field label="Note (facultatif)">
              <DictationTextarea
                key="note-perdu"
                className="input-base"
                rows={2}
                maxLength={2000}
                initialValue={form.note}
                onValueChange={(v) => patch({ note: v })}
                aria-label="Note"
              />
            </Field>
          </>
        )}

        {choice === "no_show" && (
          <>
            <Field label="Message prêt, avec ton lien de réservation Calendly">
              <DictationTextarea
                key={`absence-${draftVersion}`}
                className="input-base"
                rows={5}
                initialValue={message}
                onValueChange={(v) => {
                  messageEdited.current = true;
                  setMessage(v);
                }}
                aria-label="Message WhatsApp d'absence"
              />
              {!usablePhone && (
                <p className="mt-1.5 text-xs text-[var(--color-muted-foreground)]">
                  Pas de numéro utilisable : tu peux enregistrer l'absence, pas ouvrir WhatsApp.
                </p>
              )}
            </Field>
            <Field label="Rappeler">
              <div className="flex flex-wrap gap-2">
                {ABSENT_DAYS.map((d) => (
                  <Chip
                    key={d.key}
                    active={form.absentDay === d.key}
                    onClick={() => patch({ absentDay: d.key })}
                  >
                    {d.label}
                  </Chip>
                ))}
              </div>
            </Field>
          </>
        )}

        {error && (
          <p role="alert" className="mt-4 text-sm text-[var(--color-coral-600)]">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
